import { Injectable, OnDestroy, OnInit } from '@angular/core';
import { Observable, ReplaySubject, distinctUntilChanged } from 'rxjs';
import { eDisplayMode, eScreenSize } from '../../enums/screen-size.enum';
import { LogService } from '../log/log.service';

@Injectable({
  providedIn: 'root',
})
export class ResizeService implements OnDestroy {
  private resizeSubject = new ReplaySubject<eScreenSize>();
  private displayModeSubject = new ReplaySubject<eDisplayMode>();

  private sizes: number[];

  private currentSize: eScreenSize;
  private displayMode: eDisplayMode;

  constructor() {
    this.sizes = Object.values(eScreenSize)
      .filter((value): value is number => typeof value === 'number')
      .sort((a, b) => a - b);

    this.onWindowResize();
    window.addEventListener('resize', this.onWindowResize.bind(this));
  }

  ngOnDestroy(): void {
    window.removeEventListener('resize', this.onWindowResize);
  }

  private onWindowResize() {
    const width = window.innerWidth;
    let screenSize: eScreenSize = this.sizes[this.sizes.length - 1];

    for (let breakpoint of this.sizes) {
      if (width - 1 <= breakpoint) {
        screenSize = breakpoint;
        break;
      }
    }

    if (screenSize != this.currentSize) {
      LogService.logDebug(
        `Screen width changed, current width=${width}px, screenSize enum=${screenSize}`
      );
      this.currentSize = screenSize;
      this.resizeSubject.next(this.currentSize);
    }

    const newDisplayMode = this.isDesktop
      ? eDisplayMode.DESKTOP
      : eDisplayMode.MOBILE;

    if (this.displayMode !== newDisplayMode) {
      LogService.logDebug(
        `Display mode changed, current mode=${newDisplayMode}px`
      );
      this.displayMode = newDisplayMode;
      this.displayModeSubject.next(this.displayMode);
    }
  }

  get onResize$(): Observable<eScreenSize> {
    return this.resizeSubject.pipe(distinctUntilChanged());
  }

  get onDisplayModeChange$(): Observable<eDisplayMode> {
    return this.displayModeSubject.pipe(distinctUntilChanged());
  }

  get isMobile(): boolean {
    return this.currentSize < eScreenSize.XL;
  }

  get isDesktop(): boolean {
    return !this.isMobile;
  }
}
